<template>
  <div class="service-detail-list">
    <h1>{{ $t('service_details.txt.vehicles') }}</h1>
    <ServiceDetailSearch
      @textToSearch="textToSearchReceived"
      @selectAllByPeriod="selectAllByPeriod"
      @filterByStatus="filterByStatus"
      @filterByPeriod="filterByPeriod"
      @sortBy="sortByReceived"
    />

    <ServiceConfirmation
      ref="serviceConfirmation"
      @onConfirmation="addItemsToCart()"
    />
    <div
      v-if="!trucks && textToSearch.length == 0"
      class="loader"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div
      v-if="noSearchResults && !loading"
      class="no-results"
    >
      <v-icon>mdi-truck</v-icon>
      <br>{{ $t('service_details.txt.empty_search') }}
    </div>

    <div
      v-else-if="noSearchResults && loading"
      class="loader"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <div
      v-else-if="trucks && trucks.length > 0"
      class="results"
    >
      <div class="results_table">
        <v-row class="header">
          <v-col
            cols="1"
            class="pl-5"
          >
            <v-checkbox
              v-model="checkSelected"
              class="check-item"
              :disabled="!checkSelected"
              :ripple="false"
              @change="setSelectedTrucks"
            />
          </v-col>
          <v-col
            cols="6"
            sm="3"
            md="2"
          >
            {{ $t('service_details.txt.vehicle_name') }}
          </v-col>
          <v-col
            sm="2"
            md="2"
            class="d-none d-sm-block"
          >
            {{ $t('service_details.txt.license_plate') }}
          </v-col>
          <v-col
            sm="2"
            md="2"
            class="d-none d-md-block"
          >
            {{ $t('service_details.txt.activated') }}
          </v-col>
          <v-col
            v-if="showOwnerColumn"
            sm="2"
            md="2"
            class="d-none d-sm-block"
          >
            OWNER
          </v-col>
          <v-col
            :md="showOwnerColumn ? 1 : 2"
            class="d-none d-sm-block"
          >
            {{ $t('service_details.txt.type') }}
          </v-col>
        </v-row>
        <v-row class="body">
          <v-col
            v-for="vehicle in visibleItemsPerPage"
            :key="vehicle.subscriptionID"
            cols="12"
            :items="visibleItemsPerPage"
            class="item py-1"
          >
            <v-card
              class="pa-5"
              :class="{ selected: selected.includes(vehicle) }"
              :disabled="
                (selected.length > 0 &&
                  (selected[0].type != vehicle.type ||
                    (filterStatus.key === 'inactive' &&
                      selected[0].billingPeriod != vehicle.billingPeriod))) ||
                  disabledVehicleInCart(vehicle.subscriptionNumber)
              "
            >
              <v-container
                fluid
                class="pa-0"
              >
                <v-row>
                  <v-col
                    class="align-self-center d-flex align-center col col-1"
                  >
                    <v-checkbox
                      v-model="selected"
                      class="check-item align-self-center"
                      :disabled="cannotSelectVehicle(vehicle)"
                      :value="vehicle"
                      :ripple="false"
                      @change="setSelectedTruck"
                    />
                    <template
                      v-if="
                        service.isTachograph && cannotSelectVehicle(vehicle)
                      "
                    >
                      <v-tooltip
                        content-class="vehicle-name-tooltip"
                        bottom
                        color="slate"
                      >
                        <template v-slot:activator="{ on }">
                          <v-icon
                            color="red"
                            v-on="on"
                          >
                            mdi-alert
                          </v-icon>
                        </template>
                        <div>
                          <div>
                            <span>{{ tachographSelectionHint(vehicle) }}</span>
                          </div>
                        </div>
                      </v-tooltip>
                    </template>
                  </v-col>
                  <v-col
                    v-if="vehicle.vin || vehicle.truckName"
                    cols="5"
                    sm="3"
                    :md="2"
                    class="align-self-center ellipsis "
                  >
                    <v-tooltip
                      content-class="vehicle-name-tooltip"
                      bottom
                      color="slate"
                    >
                      <template v-slot:activator="{ on }">
                        <span
                          v-if="
                            (vehicle.truckName === null ||
                              vehicle.truckName == '') &&
                              vehicle.vin
                          "
                          class="vehicle-name"
                          @click="viewVehicleDetail(vehicle.subscriptionNumber)"
                          v-on="on"
                        >...{{
                          vehicle.vin.substring(vehicle.vin.length - 8)
                        }}</span>
                        <span
                          v-else
                          class="vehicle-name"
                          @click="viewVehicleDetail(vehicle.subscriptionNumber)"
                          v-on="on"
                        >{{ vehicle.truckName }}</span>
                      </template>
                      <div>
                        <div>
                          <span>
                            {{ $t('service_details.txt.vehicle_name') }}:
                          </span>
                          {{ vehicle.truckName }}
                        </div>
                        <div>
                          <span>
                            {{ $t('service_details.txt.vin_number') }}:
                          </span>
                          {{ vehicle.vin }}
                        </div>
                      </div>
                    </v-tooltip>
                  </v-col>
                  <v-col
                    sm="2"
                    md="2"
                    class="d-none d-sm-block text-left align-self-center"
                  >
                    {{ vehicle.plateNumber }}
                  </v-col>
                  <v-col
                    sm="2"
                    md="2"
                    class="d-none d-md-block text-left align-self-center ellipsis"
                  >
                    {{ activatedDisplay(vehicle) }}
                  </v-col>
                  <v-col
                    v-if="showOwnerColumn"
                    sm="2"
                    md="2"
                    class="d-none d-sm-block text-left align-self-center ellipsis"
                  >
                    {{ vehicle.ownerName }}
                  </v-col>
                  <v-col
                    v-if="vehicle.type == 'Month'"
                    :md="showOwnerColumn ? 1 : 2"
                    class="d-none d-md-block text-left align-self-center ellipsis"
                  >
                    {{ $t('service_details.txt.type_month') }}
                  </v-col>
                  <v-col
                    v-else-if="vehicle.type == 'Annual'"
                    :md="showOwnerColumn ? 1 : 2"
                    class="d-none d-md-block text-left align-self-center ellipsis"
                  >
                    {{ $t('service_details.txt.type_annual') }}
                  </v-col>
                  <v-col
                    v-else-if="
                      vehicle.type == 'Three Years' ||
                        vehicle.type == 'Three_Years'
                    "
                    :md="showOwnerColumn ? 1 : 2"
                    class="d-none d-md-block text-left align-self-center ellipsis"
                  >
                    {{ $t('service_details.txt.type_three_years') }}
                  </v-col>
                  <v-col
                    v-else-if="
                      vehicle.type == 'Specific Months' ||
                        vehicle.type == 'Specific_Months'
                    "
                    :md="showOwnerColumn ? 1 : 2"
                    class="d-none d-md-block text-left align-self-center ellipsis"
                  >
                    {{ $t('service_details.txt.type_ten_years') }}
                  </v-col>
                  <v-col
                    md="2"
                    class="d-none d-sm-block text-right align-self-center ml-auto"
                  >
                    <v-btn
                      v-if="!selected.includes(vehicle)"
                      class="show-button elevation-0"
                      @click="viewVehicleDetail(vehicle.subscriptionNumber)"
                    >
                      {{ $t('service_details.txt.show_vehicle') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </v-col>
        </v-row>
      </div>
      <div class="results_pagination">
        <v-pagination
          v-model="page"
          color="pale"
          prev-icon="mdi-arrow-left"
          next-icon="mdi-arrow-right"
          :length="Math.ceil(filteredList.length / itemsPerPage)"
        />
      </div>
    </div>
    <v-footer 
      fixed 
      color="white" 
      class="py-6 elevation-2"
    >
      <v-container class="py-0">
        <v-row
          v-if="!hideFooter"
          class="results_footer"
        >
          <v-col
            class="py-2 py-md-0 text-center text-sm-left align-self-center"
            cols="12"
            sm="4"
            md="2"
            lg="1"
            offset-lg="4"
          >
            <v-icon
              width="22"
              color="steel"
            >
              mdi-truck
            </v-icon>
            <span>
              x <b class="results_footer_costs">{{ selected.length }} </b>
            </span>
          </v-col>
          <v-col
            class="py-2 py-md-0 text-center text-sm-right align-self-center"
            cols="12"
            sm="8"
            md="6"
            lg="4"
          >
            <div v-if="filterStatus.key === 'active'">
              <span>
                <b class="results_footer_costs">
                  - {{ monthlyCost | formatPrice(currency.symbol) }}
                </b>
                {{ $t('service_details.txt.month') }}
                <span class="mx-3">&</span>
                <b class="results_footer_costs">
                  - {{ yearlyCost | formatPrice(currency.symbol) }}</b>
                {{ $t('service_details.txt.year') }}
              </span>
            </div>
            <div 
              v-else
              class="results_footer_chooseperiod"
            >
              <v-bottom-navigation
                v-model="initalPeriodSelection"
                class="buttons elevation-0 align-center justify-sm-end"
              >
                <span class="label">{{
                  $t('service_details.txt.choose_period')
                }}</span>
                <v-btn
                  v-if="availablePeriods.monthly"
                  v-model="activeBtn['month']"
                  class="elevation-0 ml-4 mr-2"
                  :disabled="
                    selected.length === 0 ||
                      (selected.length > 0 &&
                        selected[0].billingPeriod == 'Yearly') ||
                      !(selectedHasMonthlyPlan.length > 0)
                  "
                >
                  <span>
                    <b>{{ monthlyCost | formatPrice(currency.symbol) }}</b>
                    {{ $t('service_details.txt.month') }}
                  </span>
                </v-btn>
                <v-btn
                  v-if="availablePeriods.yearly"
                  v-model="activeBtn['annual']"
                  class="elevation-0"
                  :disabled="
                    selected.length === 0 ||
                      (selected.length > 0 &&
                        selected[0].billingPeriod == 'Monthly') ||
                      !(selectedHasYearlyPlan.length > 0)
                  "
                >
                  <span>
                    <b>{{ yearlyCost | formatPrice(currency.symbol) }}</b>
                    {{ $t('service_details.txt.year') }}
                  </span>
                </v-btn>
                <v-icon class="d-none d-md-block">
                  mdi-chevron-right
                </v-icon>
              </v-bottom-navigation>
            </div>
          </v-col>
          <v-col
            class="py-2 py-md-0 text-center text-md-right align-self-center"
            cols="12"
            md="4"
            lg="3"
          >
            <v-btn
              v-if="filterStatus.key === 'active'"
              class="results_footer_btn"
              :class="items.length == 0 ? 'disabled' : 'cancel'"
              :disabled="items.length == 0"
              @click="bulkStopService()"
            >
              {{ $t('service_details.txt.cancel') }}
            </v-btn>
            <v-btn
              v-else
              class="results_footer_btn"
              :class="
                disabledButton || items.length == 0 ? 'disabled' : 'proceed'
              "
              :disabled="disabledButton || items.length == 0"
              @click="bulkActiveService()"
            >
              {{ $t('service_details.txt.proceed') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </div>
</template>

<script>
import apiCalls from '@/helpers/apiCall'
import api from '@/helpers/api'
import ServiceDetailSearch from '@/components/ServiceDetailSearch.vue'
import ServiceConfirmation from '@/components/ServiceConfirmation.vue'
import { mapState, mapMutations, mapActions } from 'vuex'
import { tachographConfirmOptions } from '@/helpers/tachographHelper'
import visibilityHelper from '@/helpers/visibilityHelper'
import { formatDate } from '@/helpers/filters'

export default {
  name: 'ServiceDetailList',
  components: {
    ServiceDetailSearch,
    ServiceConfirmation
  },
  props: {
    service: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      productID: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 8,
      trucks: [],
      serviceInfo: [],
      selected: [],
      textToSearch: '',
      filterStatus: {
        key: 'inactive',
        text: this.$t('service_details.txt.inactive')
      },
      filterPeriod: null,
      selectedBy: '',
      sortBy: {
        key: 'activated',
        text: ''
      },
      checkSelected: false,
      activeBtn: {},
      initalPeriodSelection: -1,
      notification: false,
      notificationText: '',
      notificationType: 'success',
      loading: false,
      updatingCart: false,
      availablePeriods: { monthly: true, yearly: true },
      // DAF-130: Map to store vehicle service status (canStop, status) from TrucksGet API
      vehicleStatusMap: {},
      loadingVehicleStatus: false
    }
  },
  computed: {
    ...mapState('account', [
      'currency',
      'payload',
      'featureFlags',
      'accountRelations'
    ]),
    ...mapState('checkout', ['checkout']),
    selectedHasYearlyPlan() {
      const itemsWithAPlan = this.selected.filter(item => {
        const itemAnnual = item.totalCost.filter(cost => {
          return cost.servicePeriod === 'Annual'
        })
        return itemAnnual.length > 0
      })
      return itemsWithAPlan
    },
    selectedHasMonthlyPlan() {
      const itemsWithAPlan = this.selected.filter(item => {
        const itemAnnual = item.totalCost.filter(cost => {
          return cost.servicePeriod === 'Monthly'
        })
        return itemAnnual.length > 0
      })
      return itemsWithAPlan
    },
    noSearchResults() {
      return (
        (this.trucks && this.trucks.length == 0) ||
        (this.textToSearch.length > 0 && this.filteredList.length == 0) ||
        this.filteredList.length == 0
      )
    },
    disabledButton() {
      return !!(
        Object.keys(this.activeBtn).length === 0 ||
        (!this.activeBtn.annual && !this.activeBtn.month)
      )
    },
    monthlyCost() {
      const cost = this.items
        .filter(item => item.monthlyCost)
        .reduce((acc, item) => acc + item.monthlyCost, 0)
      if (this.filterStatus.key === 'active') {
        const activeCost = cost * -1
        this.$emit('monthlyCost', activeCost)
      } else {
        if (this.activeBtn.month) {
          this.$emit('monthlyCost', cost)
        } else {
          this.$emit('monthlyCost', 0)
        }
      }
      return cost
    },
    yearlyCost() {
      const cost = this.items
        .filter(item => item.yearlyCost)
        .reduce((acc, item) => acc + item.yearlyCost, 0)
      if (this.filterStatus.key === 'active') {
        const activeCost = cost * -1
        this.$emit('yearlyCost', activeCost)
      } else {
        if (this.activeBtn.annual) {
          this.$emit('yearlyCost', cost)
        } else {
          this.$emit('yearlyCost', 0)
        }
      }
      return cost
    },

    filteredList() {
      let filteredVehicles = this.trucks
      if (this.textToSearch) {
        const searchTerm = this.textToSearch.toLowerCase()
        filteredVehicles = filteredVehicles.filter(vehicle => {
          return (
            (vehicle.truckName &&
              vehicle.truckName.toLowerCase().includes(searchTerm)) ||
            (vehicle.plateNumber &&
              vehicle.plateNumber.toLowerCase().includes(searchTerm)) ||
            (vehicle.vin && vehicle.vin.toLowerCase().includes(searchTerm)) ||
            (vehicle.type && vehicle.type.toLowerCase().includes(searchTerm)) ||
            (vehicle.activated &&
              vehicle.activated.toLowerCase().includes(searchTerm)) ||
            (vehicle.ownerName &&
              vehicle.ownerName.toLowerCase().includes(searchTerm))
          )
        })
      }

      if (this.filterStatus.key === 'active') {
        filteredVehicles = filteredVehicles.filter(v => v.activated)
      } else {
        filteredVehicles = filteredVehicles.filter(v => !v.activated)
      }

      if (this.filterPeriod) {
        filteredVehicles = filteredVehicles.filter(
          v => v.billingPeriod === this.filterPeriod.key
        )
      }

      if (this.sortBy.key === 'vinNumber') {
        filteredVehicles.sort((x, y) => {
          return x.vin < y.vin ? -1 : x.vin > y.vin ? 1 : 0
        })
      } else if (this.sortBy.key === 'licensePlate') {
        filteredVehicles.sort((x, y) => {
          if (x.plateNumber < y.plateNumber) return -1
          if (x.plateNumber > y.plateNumber) return 1
          return 0
        })
      } else if (this.sortBy.key === 'activated') {
        filteredVehicles.sort((x, y) => {
          if (x.activated < y.activated) return -1
          if (x.activated > y.activated) return 1
          return 0
        })
      } else if (this.sortBy.key === 'type') {
        filteredVehicles.sort((x, y) => {
          if (x.type < y.type) return -1
          if (x.type > y.type) return 1
          return 0
        })
      }
      return filteredVehicles
    },

    items() {
      const items = this.selected
      const array = []
      items.forEach(i => {
        this.trucks.forEach(tr => {
          if (tr.subscriptionID === i.subscriptionID) {
            if (tr.type === 'Month') {
              tr.totalCost &&
                tr.totalCost.forEach(cost => {
                  if (cost.servicePeriod === 'Monthly') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: tr.type,
                          monthlyCost: sp.price,
                          vin: tr.vin
                        })
                      }
                    })
                  }
                })
            } else if (tr.type === 'Annual') {
              tr.totalCost &&
                tr.totalCost.forEach(cost => {
                  if (cost.servicePeriod === 'Annual') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: tr.type,
                          yearlyCost: sp.price,
                          vin: tr.vin
                        })
                      }
                    })
                  }
                })
            } else if (tr.type === 'Three Years' || tr.type === 'Three_Years') {
              tr.totalCost &&
                tr.totalCost.forEach(cost => {
                  if (cost.servicePeriod === 'Three Years') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: tr.type,
                          yearlyCost: sp.price,
                          vin: tr.vin
                        })
                      }
                    })
                  }
                })
            } else {
              tr.totalCost &&
                tr.totalCost.forEach(cost => {
                  if (cost.servicePeriod === 'Monthly') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: 'Month',
                          monthlyCost: sp.price,
                          vin: tr.vin
                        })
                      }
                    })
                  } else if (cost.servicePeriod === 'Annual') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: 'Annual',
                          yearlyCost: sp.price,
                          vin: tr.vin
                        })
                      }
                    })
                  }
                })
            }
          }
        })
      })
      return array
    },

    visibleItemsPerPage() {
      return this.filteredList.slice(
        (this.page - 1) * this.itemsPerPage,
        this.page * this.itemsPerPage
      )
    },
    vehicleInCart() {
      const array = []
      const x = this.checkout
        ? this.checkout.filter(x => x.productID == this.productID)
        : false
      if (x && x.length > 0) {
        x.forEach(product => {
          product.rateplans.forEach(rp => array.push(...rp.subscriptions))
        })
      }
      return array
    },
    hideFooter() {
      return (
        this.filterStatus.key === 'inactive' &&
        this.service.isDafConnect &&
        this.featureFlags.useEssentials
      )
    },
    showOwnerColumn() {
      return (
        this.service.subscriptionLevel__c != 'Truck' &&
        this.service.subscriptionLevel__c != 'Company' &&
        visibilityHelper.hasAccountRelations(this.accountRelations)
      )
    }
  },
  async created() {
    this.productID = this.$router.history.current.params.id
    this.setAvailablePeriods()
    await this.getVehicles()
    // DAF-130: Fetch vehicle status data to check for pending cancellations
    try {
      await this.fetchVehicleStatusData()
    } catch (error) {
      // Silently fail - vehicles remain selectable if status check fails
      console.warn('Failed to fetch vehicle status data:', error)
    }
    this.emitActivatedVehiclesCount()
  },
  methods: {
    /** Data efectiva (EffectiveStartDate) vinda do API no campo activated / Activated. */
    activatedDisplay(vehicle) {
      const raw = vehicle?.activated ?? vehicle?.Activated
      if (raw == null || String(raw).trim() === '') return ''
      const normalized = String(raw).trim()
      const ymdCandidate = normalized.includes('T')
        ? normalized.split('T')[0]
        : normalized
      const fromYmd = formatDate(ymdCandidate)
      if (fromYmd) return fromYmd
      const m = this.moment(normalized)
      if (m.isValid()) {
        return formatDate(
          `${m.format('YYYY')}-${m.format('MM')}-${m.format('DD')}`
        )
      }
      return normalized
    },
    ...mapMutations('checkout', ['addTotalCheckout', 'setCheckout']),
    ...mapActions('notification', ['setSnackbar']),
    ...mapActions('trucks', ['invalidateTrucksCache']),
    emitActivatedVehiclesCount() {
      const trucks = this.trucks
      if (!trucks || !Array.isArray(trucks)) {
        this.$emit('activatedVehiclesCount', 0)
        return
      }
      const count = trucks.filter(v => v.activated !== '').length
      this.$emit('activatedVehiclesCount', count)
    },
    getVehicles() {
      this.loading = true
      return apiCalls
        .get(api.TrucksPerServiceDetail, this.productID, '')
        .then(res => {
          this.loading = false
          if (res.status == 200) {
            this.serviceInfo = res.data
            this.trucks = res.data.trucks
          } else {
            this.serviceInfo = []
            this.trucks = []
          }
        })
    },
    /**
     * DAF-130: Fetch vehicle status data from TrucksGet API
     * This cross-references with vehicle details to get canStop and status
     * which are not available in the TrucksPerServiceDetail API response
     */
    async fetchVehicleStatusData() {
      // Only fetch for active vehicles (they are the ones that can be stopped)
      const activeVehicles = this.trucks.filter(v => v.activated)
      if (activeVehicles.length === 0) return

      this.loadingVehicleStatus = true

      // Use Promise.allSettled for fault tolerance - if one request fails,
      // others still complete and vehicle remains selectable (graceful degradation)
      const requests = activeVehicles.map(vehicle =>
        apiCalls
          .get(api.TrucksGet, vehicle.subscriptionNumber, '')
          .then(res => ({
            subscriptionNumber: vehicle.subscriptionNumber,
            response: res
          }))
          .catch(() => ({
            subscriptionNumber: vehicle.subscriptionNumber,
            response: null
          }))
      )

      const results = await Promise.allSettled(requests)

      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value.response?.status === 200) {
          const { subscriptionNumber, response } = result.value
          const vehicleData = response.data

          // Match the rate plan this row would stop (subscription rate plan id),
          // then the same sku and billing period. Sku alone is ambiguous when a
          // VIN has both the 3-year (Warning) and the monthly (True) rTDS.
          const truck = this.trucks.find(
            t => t.subscriptionNumber === subscriptionNumber
          )
          const activeService = this.matchActiveService(
            truck,
            vehicleData.activeServices
          )

          if (activeService) {
            this.$set(this.vehicleStatusMap, subscriptionNumber, {
              canStop: activeService.canStop ?? activeService.CanStop,
              status: activeService.status ?? activeService.Status,
              stoppableByWebshopUser:
                activeService.stoppableByWebshopUser ??
                activeService.StoppableByWebshopUser
            })
          }

          // DAF-451: TrucksPerServiceDetail does not include rtdsContractEndDate for active
          // vehicles. Merge it from TrucksGet so cannotSelectVehicle passes for rTDS unsubscription.
          // Only merge a valid (non-empty) string so the gate here matches cannotSelectVehicle's
          // `typeof === 'string'` check and an empty/invalid date never makes a vehicle selectable.
          if (typeof vehicleData.rtdsContractEndDate === 'string' && vehicleData.rtdsContractEndDate) {
            const truckIndex = this.trucks.findIndex(
              t => t.subscriptionNumber === subscriptionNumber
            )
            if (truckIndex !== -1) {
              this.$set(
                this.trucks[truckIndex],
                'rtdsContractEndDate',
                vehicleData.rtdsContractEndDate
              )
            }
          }
        }
      })

      this.loadingVehicleStatus = false
      this.selected = this.selected.filter(vehicle => !this.isWarningStop(vehicle))
    },
    selectAllByPeriod(period) {
      this.selectedBy = period
      this.selected = this.filteredList.filter(
        item =>
          item.billingPeriod === period &&
          !this.vehicleInCart.some(
            cartItem => cartItem.subscriptionID === item.subscriptionNumber
          ) &&
          this.cannotSelectVehicle(item) == false
      )
      this.checkSelected = this.selected.length ? true : false
    },
    setSelectedTrucks() {
      if (this.checkSelected) {
        this.selected = this.visibleItemsPerPage.filter(
          item =>
            !this.vehicleInCart.some(
              cartItem => cartItem.subscriptionID === item.subscriptionNumber
            ) &&
            this.cannotSelectVehicle(item) == false
        )
      } else {
        this.selected = []
      }
    },
    setSelectedTruck() {
      if (this.selected.length == 0) this.checkSelected = false
      else this.checkSelected = true
    },
    filterByStatus(status) {
      this.selected = []
      this.checkSelected = false
      this.filterStatus = status
      this.filterPeriod = null
    },

    filterByPeriod(period) {
      if (this.selectedBy !== period.key) {
        this.selected = []
        this.checkSelected = false
      }
      this.filterPeriod = period
    },

    textToSearchReceived(search) {
      return (this.textToSearch = search)
    },
    sortByReceived(sortBy) {
      return (this.sortBy = sortBy)
    },
    viewVehicleDetail(subscriptionNumber) {
      this.$router.push({
        name: 'vehicle_details',
        params: { id: subscriptionNumber }
      })
    },
    disabledVehicleInCart(subscriptionNumber) {
      return this.vehicleInCart.some(
        x => x.subscriptionID == subscriptionNumber
      )
    },
    bulkStopService() {
      const blocked = this.items.filter(item =>
        this.isWarningStop({ subscriptionNumber: item.subscriptionNumber })
      )
      if (blocked.length > 0) {
        this.setSnackbar({
          showing: true,
          type: 'warning',
          text: this.$t(
            'vehicle_details.txt.notification_warning_stop_three_years'
          )
        })
        this.selected = this.selected.filter(
          vehicle => !this.isWarningStop(vehicle)
        )
        return
      }

      this.items.forEach(item => {
        apiCalls
          .put(api.CartAdd, '', '', {
            subscriptionID: item.subscriptionNumber,
            productRateplanID: item.productRateplanID,
            rateplanID: item.rateplanID,
            productRateplanChargeID: item.productRateplanChargeID,
            productID: this.productID,
            addSubscription: 0,
            metaData: { '': '' }
          })
          .then(res => {
            if (res.status == 200) {
              this.setCheckout(res.data.products)
              this.invalidateTrucksCache()
              this.addTotalCheckout(this.items.length)
              this.setSnackbar({
                showing: true,
                type: 'success',
                text: this.$t('service_details.txt.notification_cancel')
              })
              this.selected = []
            } else {
              this.setSnackbar({
                showing: true,
                type: 'error',
                text: this.$t('service_details.txt.notification_error')
              })
            }
          })
      })
    },
    bulkActiveService() {
      if (this.updatingCart) return
      if (this.service.isTachograph) {
        this.$refs.serviceConfirmation.open(tachographConfirmOptions)
      } else {
        this.addItemsToCart()
      }
    },
    async addItemsToCart() {
      if (this.service.subscriptionLevel__c != 'Truck') {
        const response = await apiCalls.post(
          api.ValidateThirdPartyAvailability,
          '',
          '',
          new Set(this.items.map(i => i.vin))
        )

        if (response.data.unavailable && response.data.unavailable.length) {
          this.setSnackbar({
            showing: true,
            type: 'error',
            text: this.$t(
              'service_details.txt.notification_thirdparty_missing_base_product'
            )
          })

          this.selected = this.selected.filter(
            x => !response.data.unavailable.includes(x.vin)
          )
          return
        }
      }

      if (this.activeBtn.annual == true) {
        this.updatingCart = true
        const itemsToAdd = []
        this.items.forEach(item => {
          if (item.type === 'Annual') {
            const itemToAdd = {
              subscriptionID: item.subscriptionNumber,
              productRateplanID: item.productRateplanID,
              rateplanID: '',
              productRateplanChargeID: item.productRateplanChargeID,
              productID: this.productID,
              addSubscription: 1,
              metaData: { '': '' }
            }
            itemsToAdd.push(itemToAdd)
          }
        })
        apiCalls.put(api.CartBulkAdd, '', '', itemsToAdd).then(res => {
          this.updatingCart = false
          if (res.status == 200) {
            this.setCheckout(res.data.products)
            this.invalidateTrucksCache()
            this.addTotalCheckout(this.selected.length)
            this.setSnackbar({
              showing: true,
              type: 'success',
              text: this.$t('service_details.txt.notification_addcart')
            })
            this.selected = []
          } else {
            this.setSnackbar({
              showing: true,
              type: 'error',
              text: this.$t('service_details.txt.notification_error')
            })
          }
        })
      } else if (this.activeBtn.month == true) {
        this.updatingCart = true
        const itemsToAdd = []
        this.items.forEach(item => {
          if (item.type === 'Month') {
            const itemToAdd = {
              subscriptionID: item.subscriptionNumber,
              productRateplanID: item.productRateplanID,
              rateplanID: '',
              productRateplanChargeID: item.productRateplanChargeID,
              productID: this.productID,
              addSubscription: 1,
              metaData: { '': '' }
            }
            itemsToAdd.push(itemToAdd)
          }
        })
        apiCalls.put(api.CartBulkAdd, '', '', itemsToAdd).then(res => {
          this.updatingCart = false
          if (res.status == 200) {
            this.setCheckout(res.data.products)
            this.invalidateTrucksCache()
            this.addTotalCheckout(this.selected.length)
            this.setSnackbar({
              showing: true,
              type: 'success',
              text: this.$t('service_details.txt.notification_addcart')
            })
            this.selected = []
          } else {
            this.setSnackbar({
              showing: true,
              type: 'error',
              text: this.$t('service_details.txt.notification_error')
            })
          }
        })
      }
    },
    /**
     * Active-service row for this list entry. Prefer the subscription rate plan
     * the row would remove, then sku + billing period, then sku.
     */
    matchActiveService(vehicle, activeServices) {
      const list = Array.isArray(activeServices) ? activeServices : []
      const ratePlanId =
        vehicle?.ratePlanID || vehicle?.rateplanID || vehicle?.RatePlanID
      if (ratePlanId) {
        const byPlan = list.find(
          service =>
            (service.subscriptionRatePlanId ||
              service.SubscriptionRatePlanId) === ratePlanId
        )
        if (byPlan) return byPlan
      }

      const sameSku = list.filter(
        service => (service.sku || service.Sku) === this.service.sku
      )
      const type = vehicle?.type
      if (type) {
        const byPeriod = sameSku.find(service => {
          const period = service.servicePeriod || service.ServicePeriod || ''
          return period === type
        })
        if (byPeriod) return byPeriod
      }
      return sameSku[0]
    },
    /**
     * DAF-661: catalog flag Warning means the stop button stays visible on
     * vehicle details, but the click must not cancel. On service overview the
     * equivalent is keeping the VIN out of the stop selection.
     */
    isWarningStop(vehicle) {
      const value = this.vehicleStatusMap[vehicle?.subscriptionNumber]
        ?.stoppableByWebshopUser
      return typeof value === 'string' && value.toLowerCase() === 'warning'
    },
    tachographSelectionHint(vehicle) {
      if (this.isWarningStop(vehicle)) {
        return this.$t(
          'vehicle_details.txt.notification_warning_stop_three_years'
        )
      }
      return this.$t('service_details.txt.rtdscontract_error')
    },
    /**
     * DAF-130: Check if service cancellation is already pending for this vehicle
     * Returns true if canStop is false or status indicates pending cancellation
     */
    isPendingCancellation(vehicle) {
      if (this.filterStatus.key !== 'active') return false
      const vehicleStatus = this.vehicleStatusMap[vehicle.subscriptionNumber]
      if (!vehicleStatus) return false
      return (
        vehicleStatus.canStop === false ||
        ['Remove', 'Pending Cancellation'].includes(vehicleStatus.status)
      )
    },
    cannotSelectVehicle(vehicle) {
      // DAF-130: Check if the vehicle is already in the cart (add or remove)
      // This prevents multiple stop service actions on the same vehicle
      if (this.disabledVehicleInCart(vehicle.subscriptionNumber)) {
        return true
      }

      // DAF-130: Check if service is already pending cancellation for this vehicle
      // This prevents users from trying to stop a service that's already being stopped
      if (this.isPendingCancellation(vehicle)) {
        return true
      }

      const isThreeYears =
        vehicle.type === 'Three Years' || vehicle.type === 'Three_Years'
      const is10Years =
        vehicle.type === 'Specific Months' || vehicle.type === 'Specific_Months'

      const missingBaseProduct =
        this.filterStatus.key == 'inactive' &&
        this.service.category != 'Base Products' &&
        !vehicle.hasBaseProduct

      const deactivateBaseProductWithAddons =
        this.filterStatus.key == 'active' &&
        (this.service.isDafConnect || this.service.isLifetime) &&
        vehicle.hasAddOnProduct

      const alreadyHasAnotherBaseProduct =
        this.filterStatus.key == 'inactive' &&
        vehicle.hasBaseProduct &&
        (this.service.isDafConnect || this.service.isLifetime)

      if (this.service.isTachograph) {
        // DAF-661: fixed-term rTDS (StoppableByWebshopUser = Warning) stays
        // visible but must not be selectable for stop. Monthly rTDS (True)
        // keeps the DAF-451 path below.
        if (this.filterStatus.key === 'active' && this.isWarningStop(vehicle)) {
          return true
        }
        return !(vehicle && typeof vehicle.rtdsContractEndDate === 'string')
      }
      return (
        is10Years ||
        isThreeYears ||
        missingBaseProduct ||
        deactivateBaseProductWithAddons ||
        alreadyHasAnotherBaseProduct
      )
    },
    /**
     *Aligns the footer visible price btns with the available service rateplan periods (month, anual etc)
     */
    setAvailablePeriods() {
      this.initalPeriodSelection = null

      this.availablePeriods.monthly = this.service?.productRatePlans?.some(
        ratePlan =>
          ratePlan.billingPeriod__c == 'Monthly' &&
          ratePlan.isAllowedToSubscribe__c == 'True'
      )

      this.availablePeriods.yearly = this.service?.productRatePlans?.some(
        ratePlan =>
          ratePlan.billingPeriod__c == 'Annual' &&
          ratePlan.isAllowedToSubscribe__c == 'True'
      )

      if (this.availablePeriods.monthly && !this.availablePeriods.yearly) {
        this.initalPeriodSelection = 0 //makes monthly price btn on footer automatically selected
      }

      if (this.availablePeriods.yearly && !this.availablePeriods.monthly) {
        this.initalPeriodSelection = 1 //makes yearly price btn on footer automatically selected
      }
    }
  }
}
</script>

<style lang="scss">
.service-detail-list {
  .loader {
    text-align: center;
    padding-top: 48px;
  }
  h1 {
    margin-bottom: 22px;
    font-size: 3rem;
    font-weight: 300;
    line-height: normal;
    color: var(--v-dark-base);
    text-align: left;
  }
  .no-results {
    font-size: 1.375rem;
    line-height: normal;
    text-align: center;
    color: var(--v-bluegrey-base);
    .v-icon {
      font-size: 100px;
      color: var(--v-cloudy-base);
    }
  }
  .results {
    &_table {
      margin-bottom: 32px;
      .header {
        padding: 0 12px;
        [class*='col-'] {
          text-transform: uppercase;
          font-size: 0.8125rem;
          color: var(--v-bluegrey-base);
          padding: 10px;
        }
        .check-item {
          .v-input--selection-controls__input {
            width: 16px;
            height: 16px;
            border-radius: 4px;
            background-color: var(--v-pale-three-base);

            i {
              color: var(--v-pale-three-base);
              caret-color: var(--v-pale-three-base);
            }
          }

          &.v-input--is-disabled {
            .v-input--selection-controls__input {
              opacity: 0.6;
              i {
                &:before {
                  content: '';
                }
              }
            }
          }
        }
      }
      .body {
        .item {
          .v-card {
            border-radius: 6px;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.18);
            color: var(--v-slate-base);

            &.selected {
              background-color: var(--v-peacock-base);
              color: white;
              .v-input--selection-controls__input {
                background-color: var(--v-peacock-base) !important;
                i {
                  color: var(--v-pale-three-base) !important;
                }
              }
              .vehicle-name {
                color: white !important;
              }
            }


            .row {
              height: 56px;

              div[class*='col-'] {
                font-size: 0.875rem;
                &.ellipsis {
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
                .vehicle-name {
                  cursor: pointer;
                  color: var(--v-peacock-base);
                  text-decoration: underline;
                }

                .check-item {
                  .v-input__slot {
                    margin: 0;
                    .v-input--selection-controls__input {
                      width: 16px;
                      height: 16px;
                      border-radius: 4px;
                      background-color: var(--v-pale-three-base);

                      i {
                        color: var(--v-pale-three-base);
                      }
                    }
                  }
                }
                .show-button {
                  font-size: 0.75rem;
                  font-weight: bold;
                  text-align: center;
                  color: var(--v-peacock-base);
                  height: 32px;
                  border-radius: 3px;
                  border: solid 1px rgba(0, 92, 169, 0.3);
                  background-color: white;
                }
              }
            }
          }
        }
      }
    }
    &_pagination {
      .v-pagination {
        &__item {
          font-weight: bold;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
          margin: 0 8px;
          &:focus {
            outline: none !important;
          }
          &--active {
            color: var(--v-darkgrey-blue-base) !important;
            box-shadow: none;
          }
        }
        &__navigation {
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
          margin: 0 40px;
        }
        .v-icon {
          font-size: 1.25rem;
          color: var(--v-slate-base);
        }
      }
    }
    &_footer {
      &_btn {
        width: 100%;
        height: 48px !important;
        border-radius: 5px;
        font-weight: bold;
        text-align: center;
        &.proceed {
          background-image: linear-gradient(to bottom, #266cab 0%, #01529c);
          color: white;
        }
        &.cancel {
          background-image: linear-gradient(
            to bottom,
            #c53030 0%,
            #9b2c2c 100%
          );
          color: white;
        }
        &.disabled {
          background-color: var(--v-cloudy-base) !important;
          color: white !important;
        }
      }
      &_costs {
        font-size: 1.25rem;
        color: var(--v-slate-base);
      }
      &_chooseperiod {
        .label {
          font-size: 0.75rem;
          font-weight: bold;
          color: var(--v-peacock-base);
          text-transform: uppercase;
        }
        .buttons {
          .v-btn {
            width: 128px;
            height: 32px;
            border-radius: 3px;
            border: solid 1px var(--v-cloudy-base);
            background-color: white !important;
            font-size: 0.75rem;
            text-align: center;
            color: var(--v-steel-base);

            &.v-btn--active {
              background-color: var(--v-peacock-base) !important;
              color: white !important;
            }

            &.disabled {
              color: white !important;
            }
          }
        }
      }
    }
  }
}

.v-input--selection-controls {
  margin-top: 0px;
  padding-top: 0px;
}

.v-messages {
  min-height: 0px;
}

.vehicle-name-tooltip {
  min-height: 32px !important;
  border-radius: 16px !important;
  span {
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: bold;
    line-height: normal;
    text-align: center;
    color: white;
  }
}
</style>
